import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import _ from 'lodash';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { useCallback, useLayoutEffect } from 'react';

import type { AssignableRef } from '@adflow/frontend/util-common';
import { useForwardingRef, useRefMap } from '@adflow/frontend/util-common';

const sides = ['top', 'bottom', 'left', 'right'] as const;
type Side = (typeof sides)[number];

const styles = {
	root: css({ position: 'relative' }),
	scrollArea: css({
		width: '100%',
		height: '100%',
		overflow: 'auto',
		isolation: 'isolate',
	}),

	shadowOverlay: (side: Side) =>
		css({
			pointerEvents: 'none',
			backgroundImage: `linear-gradient(to ${side}, #00000000, #00000020)`,
			position: 'absolute',
			// Set opacity to 0 initially so that the shadows are not visible before they are positioned.
			opacity: 0,
		}),
};

export interface ScrollShadowBoxProps extends HTMLAttributes<HTMLDivElement> {
	height?: CSSProperties['height'];
	outerStyle?: CSSInterpolation;
	scrollAreaStyle?: CSSInterpolation;
	children?: ReactNode;

	scrollAreaRef?: AssignableRef<HTMLDivElement | null>;

	scrollAreaTestid?: string;

	topOffset?: number;
	bottomOffset?: number;
	leftOffset?: number;
	rightOffset?: number;

	sidesDisabled?: Side[];
}

/**
 * A transparent box that shows shadows on the sides where its content overflows.
 */
export function ScrollShadowBox(props: ScrollShadowBoxProps) {
	const {
		children,
		scrollAreaStyle,
		height,
		outerStyle,

		scrollAreaRef: propsScrollAreaRef,

		scrollAreaTestid,

		topOffset,
		bottomOffset,
		leftOffset,
		rightOffset,

		sidesDisabled = [],

		...rest
	} = props;

	const scrollAreaRef = useForwardingRef<HTMLDivElement | null>(null, [
		propsScrollAreaRef,
	]);

	const shadowRefs = useRefMap<Side, HTMLDivElement>();

	const updateScrollShadows = useCallback(() => {
		if (!scrollAreaRef.current) return;

		const element = scrollAreaRef.current;

		const offset = {
			top: topOffset ?? 0,
			bottom:
				(bottomOffset ?? 0) + (element.offsetHeight - element.clientHeight),
			left: leftOffset ?? 0,
			right: (rightOffset ?? 0) + element.offsetWidth - element.clientWidth,
		};

		const scrollDistance = {
			top: element.scrollTop,
			bottom: element.scrollHeight - element.clientHeight - element.scrollTop,
			left: element.scrollLeft,
			right: element.scrollWidth - element.clientWidth - element.scrollLeft,
		};

		// Because this could be called 60 times a second, use imperative updates
		// instead of useState to avoid potential performance issues from
		// re-rendering the component and re-creating Emotion styles.
		for (const side of sides) {
			const style: Partial<CSSStyleDeclaration> = {
				opacity: _.clamp(scrollDistance[side] / 30, 0, 1).toString(),
				...(side === 'top' || side === 'bottom'
					? {
							height: '0.5rem',
							left: `${offset.left}px`,
							right: `${offset.right}px`,
							[side]: `${offset[side]}px`,
						}
					: {
							width: '0.5rem',
							top: `${offset.top}px`,
							bottom: `${offset.bottom}px`,
							[side]: `${offset[side]}px`,
						}),
			};

			const refToUpdate = shadowRefs(side).current;
			if (refToUpdate) {
				Object.assign(
					refToUpdate.style,
					sidesDisabled.includes(side) ? null : style
				);
			}
		}
	}, [
		bottomOffset,
		leftOffset,
		rightOffset,
		scrollAreaRef,
		shadowRefs,
		sidesDisabled,
		topOffset,
	]);

	useLayoutEffect(() => {
		// Set shadows on initial render and offset changes.
		updateScrollShadows();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bottomOffset, leftOffset, rightOffset, topOffset]);

	return (
		<div css={[styles.root, outerStyle]} style={{ height }}>
			<div
				{...rest}
				ref={scrollAreaRef}
				onScroll={updateScrollShadows}
				css={[styles.scrollArea, scrollAreaStyle]}
				data-scrollarea
				data-testid={scrollAreaTestid}
				id={scrollAreaTestid}
			>
				{children}
			</div>

			{sides.map((side) => (
				<div
					key={side}
					ref={shadowRefs(side)}
					css={styles.shadowOverlay(side)}
				/>
			))}
		</div>
	);
}
